import { useEffect, useMemo, useState } from 'react';
import type React from 'react';

import type { TransitionStatus } from 'react-transition-state';
import { useTransitionMap } from 'react-transition-state';

import type { HeroFeedItem } from 'src/app/pages/promote/pageContext';

import { HeroFeedItemPill } from './components/HeroFeedItemPill';
import { useHeroFeed } from './hooks/useHeroFeed';
import { useHeroFeedView } from './hooks/useHeroFeedView';

export const HeroFeed: React.FC = () => {
	const {
		data: { displayedItems, animateSpeed, animateDelay },
	} = useHeroFeed({ pillDisplayedCountMin: 1 });

	useHeroFeedView();

	const { stateMap, setItem, toggle } = useTransitionMap<string>({
		timeout: animateDelay + animateSpeed,
		unmountOnExit: true,
	});

	// Keep a map of items by ID so we can render exiting items
	const [itemsMap, setItemsMap] = useState<Map<string, HeroFeedItem>>(() => new Map());

	useEffect(() => {
		const currentIds = new Set(displayedItems.map((item) => item.event.id));

		// Update items map with new items
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setItemsMap((prev) => {
			const next = new Map(prev);
			for (const item of displayedItems) {
				next.set(item.event.id, item);
			}
			return next;
		});

		// Add new items to transition map
		for (const item of displayedItems) {
			if (!stateMap.has(item.event.id)) {
				setItem(item.event.id, { initialEntered: true });
			}
		}

		// Trigger exit animation for items that are no longer in the list
		for (const [id, state] of stateMap) {
			if (!currentIds.has(id) && state.isEnter) {
				toggle(id, false);
			}
		}
	}, [displayedItems, setItem, toggle, stateMap]);

	// Build the list of items to render (includes exiting items)
	const itemsToRender = useMemo(() => {
		const result: Array<{ id: string; item: HeroFeedItem; status: TransitionStatus }> = [];

		// First, add items from stateMap (includes exiting items)
		for (const [id, state] of stateMap) {
			if (state.status !== 'unmounted') {
				const item = itemsMap.get(id);
				if (item) {
					result.push({ id, item, status: state.status });
				}
			}
		}

		// On first render, stateMap is empty - render displayedItems directly as "entered"
		if (result.length === 0) {
			for (const item of displayedItems) {
				result.push({ id: item.event.id, item, status: 'entered' });
			}
		}

		return result;
	}, [stateMap, itemsMap, displayedItems]);

	if (!displayedItems.length && !itemsToRender.length) return null;

	return (
		<div className="relative overflow-hidden whitespace-nowrap" data-testid="hero-feed">
			<div role="list" className="size-full overflow-hidden">
				{itemsToRender.map(({ id, item, status }) => (
					<HeroFeedItemPill
						key={id}
						role="listitem"
						item={item}
						animateDelay={animateDelay}
						animateSpeed={animateSpeed}
						status={status}
					/>
				))}
			</div>
		</div>
	);
};
